import {Trans} from '@lingui/macro';
import React, {useMemo} from 'react';

import {useInitialEffect} from '../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {fromEntries} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {TeammateAvatar} from '../../../../components/common/avatars/teammateAvatar';
import {computeIsGlobalNamespace, isTeammateNamespace} from '../../../../helpers/models/namespaceHelpers';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {CircleModel, CirclePublicId} from '../../../../models/api/circleModel';
import {TeammateAccountStatusesEnum, TeammateModel} from '../../../../models/api/teammateModel';
import {buildPublicId} from '../../../../models/applications/publicIdTypes';
import {getFlatTeammates, getUser} from '../../../../store/data/teammates/teammatesSelectors';
import {getHasCircles} from '../../../../store/featureFlags/featureFlagsSelectors';
import {useBaseEditorContext} from '../../../editors/baseEditorContext';
import {filterTeammateExpressions} from '../../../editors/expressions/expressionInferHelpers';
import {useFetchCircles} from '../../../fetchers/circles';
import {SelectedDropdownItem} from '../dropdowns/dropdownWithExpressions';
import {MultipleChoiceDropdown} from '../dropdowns/multipleChoiceDropdown';
import {DropdownGroup} from '../dropdowns/multipleGroupsDropdownHelpers';
import {SingleChoiceDropdown} from '../dropdowns/singleChoiceDropdown';
import {
  defaultDropdownWidth,
  GenericMultipleParameterProps,
  GenericSingleParameterProps,
} from './genericParameter';

/*
 * Types.
 */

export type TeammateSelectedDropdownItem = SelectedDropdownItem<TeammateModel, [CirclePublicId]>;

/*
 * Component.
 */

export const TeammateParameter = (props: GenericSingleParameterProps<TeammateModel>) => {
  const teammates = useTeammates();
  const teammateExpressions = useMemo(
    () => filterTeammateExpressions(props.expressions, false),
    [props.expressions],
  );

  /**
   * On initial mount, we should call onChange as triggers stores `any teammate` as undefined.
   * Without doing that, the `any teammate` will be checked in dropdown but validation will fail.
   */
  useInitialEffect(() => {
    props.onChange(props.selectedItem);
  });

  return (
    <SingleChoiceDropdown
      {...props}
      expressions={teammateExpressions}
      items={teammates}
      title={props.onSelectCreateExpression ? <Trans id="rule-editor-teammate-group">Teammate</Trans> : null}
      getItemKey={(item) => String(item.id)}
      renderItemLabel={(item) => (item.givenName ? `${item.givenName} ${item.familyName}` : item.email)}
      renderItemIcon={(item) => <TeammateAvatar teammate={item} size={VisualSizesEnum.MEDIUM} />}
      serializeItemForSearch={(item) =>
        item.givenName ? `${item.givenName} ${item.familyName}` : item.email
      }
      width={defaultDropdownWidth}
      placeholder={<Trans id="rule-editor-select-teammate">Select a teammate</Trans>}
    />
  );
};

export const TeammatesParameter = (props: GenericMultipleParameterProps<TeammateModel, [CirclePublicId]>) => {
  const teammates = useTeammates();
  const teammateExpressions = useMemo(
    () => filterTeammateExpressions(props.expressions, true),
    [props.expressions],
  );

  const hasCircles = useAppSelector(getHasCircles);
  const {circles} = useFetchCircles(undefined, {enabled: hasCircles});

  const circleEntries = useMemo(
    () =>
      circles.map((circle): [CirclePublicId, CircleModel] => [buildPublicId('circle', circle.id), circle]),
    [circles],
  );

  const circleMap = useMemo(() => fromEntries(circleEntries), [circleEntries]);

  const circleGroup = useMemo(
    (): DropdownGroup<CirclePublicId> => ({
      title: <Trans id="rule-teammate-parameter-circle-title">Teammate group</Trans>,
      items: circleEntries.map(([publicId]) => publicId),
      getItemKey: (item) => item,
      renderItemLabel: (item) => circleMap[item].name,
      renderItemIcon: () => <Icon name="group" />,
      serializeItemForSearch: (item) => circleMap[item].name,
    }),
    [circleEntries, circleMap],
  );

  const additionalGroups = useMemo(() => [circleGroup] as const, [circleGroup]);

  return (
    <MultipleChoiceDropdown<TeammateModel, [CirclePublicId]>
      {...props}
      expressions={teammateExpressions}
      items={teammates}
      additionalGroups={additionalGroups}
      title={
        props.onSelectCreateExpression ? <Trans id="rule-editor-teammates-group">Teammates</Trans> : null
      }
      selectedItems={props.selectedItems === undefined ? [] : props.selectedItems} // Some legacy rules used to save `any teammate` as `undefined` instead of `[]`
      getItemKey={(item) => String(item.id)}
      renderItemLabel={(item) => (item.givenName ? `${item.givenName} ${item.familyName}` : item.email)}
      renderItemIcon={(item) => <TeammateAvatar teammate={item} size={VisualSizesEnum.MEDIUM} />}
      serializeItemForSearch={(item) =>
        item.givenName ? `${item.givenName} ${item.familyName}` : item.email
      }
      placeholder={<Trans id="rule-editor-select-teammates">Select teammates</Trans>}
      anyLabel={<Trans id="rule-editor-any-teammate">Any teammate</Trans>}
      anyIcon={<Icon name="teammates" />}
      sortSelectedItemsToTop
      width={defaultDropdownWidth}
    />
  );
};

/*
 * Helpers.
 */

// @RULES TODO: In future not all teammates might be in redux, better would be to get this form api and paginate
// https://frontapp.slack.com/archives/C20LA671C/p1620229222126200?thread_ts=1620227895.115400&cid=C20LA671C
function useTeammates() {
  const user = useAppSelector(getUser);
  const {namespace} = useBaseEditorContext();
  const activeTeammates = useAppSelector(getFlatTeammates).filter(
    (t) => t.accountStatus === TeammateAccountStatusesEnum.ACTIVE,
  );
  const filteredTeammates = isTeammateNamespace(namespace)
    ? activeInCommonTeam(activeTeammates, user)
    : activeInNamespace(activeTeammates, user, namespace);

  return [user, ...filteredTeammates.filter((t) => t.id !== user.id)];
}

function activeInCommonTeam(teammates: ReadonlyArray<TeammateModel>, user: TeammateModel) {
  const userSharedNamespaces = getSharedNamespaces(user);
  return teammates.filter((t) => getSharedNamespaces(t).some((ns) => userSharedNamespaces.includes(ns)));
}

function activeInNamespace(teammates: ReadonlyArray<TeammateModel>, user: TeammateModel, namespace: string) {
  if (namespace === user.namespace) {
    return activeInCommonTeam(teammates, user);
  }

  return teammates.filter((t) => Object.keys(t.access).includes(namespace));
}

function getSharedNamespaces(teammate: TeammateModel) {
  return Object.keys(teammate.access || {}).filter(
    (ns: string) => !computeIsGlobalNamespace(ns) && ns !== teammate.namespace,
  );
}
